/**
 * marketEventsService -- macro/earnings events calendar.
 *
 * Hits GET /market/events?days=14, which reads forward-looking events from
 * market-events-prod. JWT-required, no entitlement gate. Used by the
 * Notches Events page and by alert dispatchers (via the eventContext
 * server-side helper, not this client) to surface event proximity.
 *
 * 10-minute cache because the calendar is daily-refreshed upstream and
 * subscribers may navigate the page repeatedly.
 */
import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export type Country = 'US' | 'EU' | 'UK' | 'JP' | 'CN' | 'CA' | 'OPEC';
export type ImpactTier = 'tier1' | 'tier2' | 'tier3';

export interface MarketEvent {
  eventDate: string;       // YYYY-MM-DD (ET calendar date)
  eventTime: string;       // ISO-8601 with TZ offset
  country: Country;
  category: string;        // fomc | cpi | nfp | pce | ppi | ecb | boj | boe | boc | china | earnings | treasury-auction | vix-expiration | opec
  name: string;
  impactTier: ImpactTier;
  affectedSymbols: string[];
  previous?: string | null;
  estimate?: string | null;
  actual?: string | null;
  unit?: string | null;
  source: string;
}

export interface MarketEventsResponse {
  from: string;            // YYYY-MM-DD
  to: string;              // YYYY-MM-DD
  events: MarketEvent[];
}

const CACHE_TTL_MS = 10 * 60 * 1000;
let cache: { at: number; days: number; payload: MarketEventsResponse } | null = null;

export const marketEventsService = {
  async get(token: string, days: number = 14): Promise<MarketEventsResponse> {
    if (cache && cache.days === days && Date.now() - cache.at < CACHE_TTL_MS) {
      return cache.payload;
    }
    const resp = await axios.get<MarketEventsResponse>(
      `${API_BASE_URL}/market/events?days=${days}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    cache = { at: Date.now(), days, payload: resp.data };
    return resp.data;
  },
};
